import DatePicker from 'vue2-datepicker'
import CryptoJS from 'crypto-js'
import moment from 'moment'
import VueElementLoading from 'vue-element-loading'
import axios from 'axios'
import apiUrl from '../../../constants'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'productivity-tracking',
  components: { DatePicker, VueElementLoading, apexchart: VueApexCharts },

  data() {
    return {
      isLoading: false,
      hubId: null,
      hubName: '',
      selectedDate: moment().subtract(1, 'days').toDate(),
      selectedDays: 1,
      searchId: '',
      moment: moment,
      sortBy: 'name', // 'name', 'performance'
      sortOrder: 'asc', // 'asc', 'desc'

      summary: {
        totalScans: 0,
        totalMHR: 0,
        raygCounts: { R: 0, A: 0, Y: 0, G: 0 },
        period: "Yesterday"
      },

      perfData: [],
      raygData: [],
      hubAnalyticsData: [],
      hubHourlyDayData: [],
      hubCompareLast7Data: [],
      hubCompareLastWeekData: [],
      hubCompareMeta: {
        lastWeekDate: null,
        lastWeekLabel: ''
      },
      expandedEmp: null,
      globalViewMode: 'performance', // 'performance' | 'rayg' | 'geographic' | 'hubAnalytics'
      viewMode: 'matrix', // 'matrix' (hourly) or 'summary' (daily)

      currentPage: 1,
      itemsPerPage: 10,
      selectedStatus: null, // 'R', 'A', 'Y', 'G', 'B'
      showTypeDropdown: false,
      showHubCategoryDropdown: false,
      showHubBucketDropdown: false,

      // Separate filters
      perfFilters: {
        date: moment().subtract(1, 'days').toDate(),
        days: 1,
        searchId: '',
        status: null,
        selectedTypes: []
      },
      raygFilters: {
        date: moment().subtract(1, 'days').toDate(),
        days: 1,
        searchId: '',
        status: null,
        selectedTypes: []
      },
      geoFilters: {
        fromDate: moment().subtract(1, 'days').toDate(),
        toDate: moment().subtract(1, 'days').toDate(),
        zone_name: '',
        state_name: '',
        city_name: '',
        facility_id: '',
        searchId: '',
        status: null,
        selectedTypes: []
      },
      hubAnalyticsFilters: {
        date: moment().subtract(1, 'days').toDate(),
        days: 1,
        compareWeeksAgo: 1,
        selectedTypes: [],
        selectedCategories: [],
        selectedBuckets: []
      }
    }
  },

  computed: {
    activeFilters() {
      if (this.globalViewMode === 'performance') return this.perfFilters
      if (this.globalViewMode === 'geographic') return this.geoFilters
      if (this.globalViewMode === 'hubAnalytics') return this.hubAnalyticsFilters
      return this.raygFilters
    },

    availableEmployeeTypes() {
      let rawData = this.raygData
      if (this.globalViewMode === 'performance') rawData = this.perfData
      else if (this.globalViewMode === 'hubAnalytics') rawData = this.hubAnalyticsData
      if (!rawData) return []
      const types = rawData.map(e => e.employeeType || 'Other')
      return [...new Set(types)].filter(Boolean).sort()
    },

    baseFilteredData() {
      const filters = this.activeFilters
      if (!filters) return []

      const searchId = filters.searchId || ''
      const id = searchId.toString().trim().toLowerCase()
      let data = this.processedData || []

      if (id) {
        data = data.filter(e => {
          if (!e) return false
          const empId = (e.employeeId || '').toString().toLowerCase()
          const empCode = (e.empCode || '').toString().toLowerCase()
          const empName = (e.employeeName || '').toString().toLowerCase()
          const empEmail = (e.email || '').toString().toLowerCase()

          return empId.includes(id) || 
                 empCode.includes(id) || 
                 empName.includes(id) || 
                 empEmail.includes(id)
        })
      }

      // Filter by Employee Type (Multi-select)
      if (filters.selectedTypes && filters.selectedTypes.length > 0) {
        data = data.filter(e => e && filters.selectedTypes.includes(e.employeeType || 'Other'))
      }

      return data
    },

    raygCounts() {
      const data = this.baseFilteredData || []
      let r = 0, a = 0, y = 0, g = 0, b = 0, n = 0;
      data.forEach(emp => {
        if (!emp || !emp.aggregate) return
        const status = emp.aggregate.rayg
        if (status === 'N') n++
        else if (status === 'R') r++
        else if (status === 'A') a++
        else if (status === 'Y') y++
        else if (status === 'G') g++
        else if (status === 'B') b++
      })
      return { R: r, A: a, Y: y, G: g, B: b, N: n }
    },

    summaryDates() {
      const filters = this.activeFilters
      if (filters.days === 1) return []
      const dates = []
      for (let i = 0; i < filters.days; i++) {
        dates.push(moment(filters.date).subtract(i, 'days').format('DD/MM (ddd)'))
      }
      return dates.reverse()
    },

    processedData() {
      const dataToProcess = this.globalViewMode === 'performance' ? this.perfData : this.raygData
      if (!dataToProcess) return []
      
      return dataToProcess.map(emp => {
        if (!emp) return { buckets: [], aggregate: {} }
        const aggregateHourly = Array(24).fill(0)
        let totalScans = 0

        const categoryGroups = {}

        const buckets = emp.buckets || []
        buckets.forEach(b => {
          if (!b) return
          const hourlyScans = b.hourlyScans || Array(24).fill(0)
          hourlyScans.forEach((val, hour) => {
            aggregateHourly[hour] += val
          })
          totalScans += b.totalScans || 0

          if (!categoryGroups[b.mainBucket]) {
            categoryGroups[b.mainBucket] = {
              name: b.mainBucket,
              totalScans: 0,
              buckets: [],
              hourlyTotal: Array(24).fill(0)
            }
          }
          categoryGroups[b.mainBucket].totalScans += b.totalScans || 0
          categoryGroups[b.mainBucket].buckets.push(b.bucketName)

          hourlyScans.forEach((val, hour) => {
            categoryGroups[b.mainBucket].hourlyTotal[hour] += val
          })
        })

        const aggregate = Object.assign({}, emp.aggregate || {}, {
          avgMHR: ((emp.aggregate ? emp.aggregate.avgMHR : 0) || 0).toFixed(2),
          prodIndex: ((emp.aggregate ? emp.aggregate.prodIndex : 0) || 0).toFixed(2)
        })

        if (totalScans === 0) {
          aggregate.rayg = 'N'
          aggregate.totalScans = 0
        }

        return Object.assign({}, emp, {
          categoryGroups: emp.categoryGroups || Object.values(categoryGroups),
          aggregate: aggregate
        })
      })
    },

    filteredData() {
      const filters = this.activeFilters
      if (!filters) return []
      let data = this.baseFilteredData || []

      if (filters.status) {
        data = data.filter(e => e && e.aggregate && e.aggregate.rayg === filters.status)
      } else {
        // By default, only list active scanning employees (scans > 0)
        data = data.filter(e => e && e.aggregate && e.aggregate.totalScans > 0)
      }

      return data
    },

    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredData.slice(start, end)
    },

    totalPages() {
      return Math.ceil(this.filteredData.length / this.itemsPerPage) || 1
    },

    hubAnalyticsEmployees() {
      let employees = this.hubAnalyticsData || []
      const types = this.hubAnalyticsFilters.selectedTypes
      if (types && types.length) {
        employees = employees.filter(e => types.includes(e.employeeType || 'Other'))
      }
      return employees
    },

    availableHubCategories() {
      const set = {}
      ;(this.hubAnalyticsEmployees || []).forEach(emp => {
        ;(emp.buckets || []).forEach(b => {
          if (b && b.mainBucket) set[b.mainBucket] = true
        })
      })
      return Object.keys(set).sort()
    },

    availableHubBuckets() {
      const cats = this.hubAnalyticsFilters.selectedCategories || []
      const set = {}
      ;(this.hubAnalyticsEmployees || []).forEach(emp => {
        ;(emp.buckets || []).forEach(b => {
          if (!b || !b.bucketName) return
          if (cats.length && cats.indexOf(b.mainBucket || 'Other') === -1) return
          set[b.bucketName] = true
        })
      })
      return Object.keys(set).sort()
    },

    hubBucketFilterActive() {
      const f = this.hubAnalyticsFilters
      return (f.selectedCategories && f.selectedCategories.length > 0) ||
        (f.selectedBuckets && f.selectedBuckets.length > 0)
    },

    hubAnalyticsSummary() {
      const employees = this.hubAnalyticsEmployees
      const days = this.hubAnalyticsFilters.days || 1
      const selectedCats = this.hubAnalyticsFilters.selectedCategories || []
      const selectedBuckets = this.hubAnalyticsFilters.selectedBuckets || []
      const hasBucketFilter = selectedCats.length > 0 || selectedBuckets.length > 0

      const hourlyTotals = Array(24).fill(0)
      const raygCounts = { R: 0, A: 0, Y: 0, G: 0, B: 0, N: 0 }
      const workstreamMap = {}
      const categoryMap = {}
      const bucketMap = {}
      let totalScans = 0
      let totalMHR = 0
      let prodIndexSum = 0
      let activeCount = 0

      const bucketMatches = (b) => {
        if (!b) return false
        if (!hasBucketFilter) return true
        const cat = b.mainBucket || 'Other'
        const name = b.bucketName
        if (selectedBuckets.length && selectedBuckets.indexOf(name) !== -1) return true
        if (selectedBuckets.length && selectedCats.length === 0) return false
        if (selectedCats.length && selectedCats.indexOf(cat) !== -1) {
          // category selected: include all buckets in cat unless specific buckets also selected
          if (!selectedBuckets.length) return true
          return selectedBuckets.indexOf(name) !== -1
        }
        return false
      }

      employees.forEach(emp => {
        if (!emp || !emp.aggregate) return
        const matchedBuckets = (emp.buckets || []).filter(bucketMatches)

        let scans = 0
        let mhr = 0
        const hourly = Array(24).fill(0)

        if (hasBucketFilter) {
          matchedBuckets.forEach(b => {
            scans += b.totalScans || 0
            mhr += parseFloat(b.totalMHR || 0)
            ;(b.hourlyScans || []).forEach((val, h) => {
              if (h >= 0 && h < 24) hourly[h] += val || 0
            })
          })
        } else {
          const agg = emp.aggregate
          scans = agg.totalScans || 0
          mhr = parseFloat(agg.totalMHR || 0)
          ;(agg.hourlyScans || []).forEach((val, h) => {
            if (h >= 0 && h < 24) hourly[h] += val || 0
          })
        }

        const status = scans === 0
          ? 'N'
          : (hasBucketFilter
            ? (mhr < 3 * days ? 'R' : mhr < 5 * days ? 'A' : mhr < 7 * days ? 'Y' : mhr < 8 * days ? 'G' : 'B')
            : (emp.aggregate.rayg || 'R'))

        if (raygCounts[status] !== undefined) raygCounts[status]++
        totalScans += scans
        totalMHR += mhr

        if (scans > 0) {
          activeCount++
          prodIndexSum += hasBucketFilter
            ? (mhr / (8 * days)) * 100
            : parseFloat(emp.aggregate.prodIndex || 0)
        }

        hourly.forEach((val, h) => { hourlyTotals[h] += val })

        const ws = emp.workstream || 'Unassigned'
        if (!workstreamMap[ws]) workstreamMap[ws] = { workstream: ws, employees: 0, active: 0, scans: 0, mhr: 0 }
        workstreamMap[ws].employees++
        if (scans > 0) workstreamMap[ws].active++
        workstreamMap[ws].scans += scans
        workstreamMap[ws].mhr += mhr

        const bucketsForMaps = hasBucketFilter ? matchedBuckets : (emp.buckets || [])
        bucketsForMaps.forEach(b => {
          if (!b) return
          const cat = b.mainBucket || 'Other'
          if (!categoryMap[cat]) categoryMap[cat] = { category: cat, scans: 0, mhr: 0 }
          categoryMap[cat].scans += b.totalScans || 0
          categoryMap[cat].mhr += parseFloat(b.totalMHR || 0)

          const bName = b.bucketName || 'Unknown'
          if (!bucketMap[bName]) {
            bucketMap[bName] = {
              bucketName: bName,
              category: cat,
              scans: 0,
              mhr: 0,
              hourly: Array(24).fill(0)
            }
          }
          bucketMap[bName].scans += b.totalScans || 0
          bucketMap[bName].mhr += parseFloat(b.totalMHR || 0)
          ;(b.hourlyScans || []).forEach((val, h) => {
            if (h >= 0 && h < 24) bucketMap[bName].hourly[h] += val || 0
          })
        })
      })

      const rosterCount = employees.length
      const expectedMHR = activeCount * 8 * days
      const hubPerformanceScore = expectedMHR > 0
        ? Math.min(100, Math.round((totalMHR / expectedMHR) * 100))
        : 0
      const avgProdIndex = activeCount > 0 ? (prodIndexSum / activeCount).toFixed(1) : '0.0'
      const avgMhrPerActive = activeCount > 0 ? (totalMHR / activeCount).toFixed(2) : '0.00'
      const utilizationPct = rosterCount > 0 ? Math.round((activeCount / rosterCount) * 100) : 0

      const workstreams = Object.values(workstreamMap).sort((a, b) => b.scans - a.scans)
      const categories = Object.values(categoryMap).sort((a, b) => b.scans - a.scans)
      const buckets = Object.values(bucketMap).sort((a, b) => b.scans - a.scans)
      const peakHour = hourlyTotals.indexOf(Math.max.apply(null, hourlyTotals))

      return {
        rosterCount,
        activeCount,
        totalScans,
        totalMHR: totalMHR.toFixed(2),
        avgMhrPerActive,
        avgProdIndex,
        utilizationPct,
        hubPerformanceScore,
        raygCounts,
        hourlyTotals,
        peakHour: peakHour >= 0 && totalScans > 0 ? peakHour : null,
        workstreams,
        categories,
        buckets,
        filterActive: hasBucketFilter,
        periodLabel: days === 1 ? 'Daily' : `${days}-Day`
      }
    },

    hubHourlyCompareSeries() {
      const last7 = this.aggregateHubHourlyTotals(this.hubCompareLast7Data)
      const lastWeek = this.aggregateHubHourlyTotals(this.hubCompareLastWeekData)
      // last7 fetch is days=7 sum → convert to daily average per hour
      const last7Avg = last7.map(v => Math.round((v / 7) * 10) / 10)
      return {
        last7Avg,
        lastWeekSameDay: lastWeek,
        lastWeekLabel: this.hubCompareMeta.lastWeekLabel || 'Same weekday last week'
      }
    },

    hubHourlyChartOptions() {
      const scope = this.hubBucketFilterActive ? 'Selected buckets' : 'All buckets'
      return {
        chart: {
          type: 'line',
          height: 300,
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: true }
        },
        stroke: {
          curve: 'smooth',
          width: [3, 2, 2],
          // solid selected day · dotted 7-day avg · solid last-week same weekday
          dashArray: [0, 6, 0]
        },
        colors: ['#FF6700', '#64748b', '#2563eb'],
        dataLabels: { enabled: false },
        markers: {
          size: [0, 0, 3],
          hover: { size: 5 }
        },
        xaxis: {
          categories: Array.from({ length: 24 }, (_, i) => `${i}:00`),
          labels: { style: { fontSize: '10px' } }
        },
        yaxis: {
          title: { text: `${scope} · scans / hour`, style: { fontSize: '11px', fontWeight: 600 } },
          min: 0
        },
        legend: {
          position: 'top',
          horizontalAlign: 'right',
          fontSize: '11px',
          fontWeight: 600
        },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
        tooltip: {
          shared: true,
          intersect: false
        }
      }
    },

    hubHourlyChartSeries() {
      const cmp = this.hubHourlyCompareSeries
      // Always plot the analysis calendar day (not multi-day sum) for fair weekday compare
      const selectedDayHourly = this.hubAnalyticsFilters.days === 1
        ? this.hubAnalyticsSummary.hourlyTotals
        : this.aggregateHubHourlyTotals(this.hubHourlyDayData)
      return [
        { name: 'Selected day', data: selectedDayHourly },
        { name: 'Last 7 days avg', data: cmp.last7Avg },
        { name: cmp.lastWeekLabel, data: cmp.lastWeekSameDay }
      ]
    },

    hubRaygChartOptions() {
      return {
        chart: { type: 'donut', height: 300 },
        labels: ['0-3 Hrs', '3-5 Hrs', '5-7 Hrs', '7-8 Hrs', '8+ Hrs', 'No scan'],
        colors: ['#ef4444', '#f59e0b', '#eab308', '#10b981', '#3b82f6', '#9ca3af'],
        legend: { position: 'bottom', fontSize: '11px' },
        dataLabels: { enabled: true, formatter: (val) => `${Math.round(val)}%` },
        plotOptions: {
          pie: {
            donut: {
              size: '62%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Roster',
                  formatter: () => String(this.hubAnalyticsSummary.rosterCount)
                }
              }
            }
          }
        }
      }
    },

    hubRaygChartSeries() {
      const c = this.hubAnalyticsSummary.raygCounts
      return [c.R, c.A, c.Y, c.G, c.B, c.N]
    },

    hubWorkstreamChartOptions() {
      const ws = this.hubAnalyticsSummary.workstreams.slice(0, 8)
      return {
        chart: { type: 'bar', height: 280, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '65%' } },
        colors: ['#6366f1'],
        dataLabels: { enabled: false },
        xaxis: {
          categories: ws.map(w => w.workstream),
          labels: { style: { fontSize: '11px' } }
        },
        grid: { borderColor: '#f1f5f9' },
        tooltip: { theme: 'dark' }
      }
    },

    hubWorkstreamChartSeries() {
      const ws = this.hubAnalyticsSummary.workstreams.slice(0, 8)
      return [{ name: 'Scans', data: ws.map(w => w.scans) }]
    }
  },

  watch: {
    'perfFilters.date'() {
      this.currentPage = 1
      this.loadData('performance')
    },
    'perfFilters.days'() {
      this.currentPage = 1
      this.loadData('performance')
    },
    'raygFilters.date'() {
      this.loadData('rayg')
    },
    'raygFilters.days'() {
      this.loadData('rayg')
    },
    'hubAnalyticsFilters.date'() {
      this.loadData('hubAnalytics')
    },
    'hubAnalyticsFilters.days'() {
      this.loadData('hubAnalytics')
    },
    'hubAnalyticsFilters.compareWeeksAgo'() {
      this.loadData('hubAnalytics')
    },
    globalViewMode(newMode) {
      this.showTypeDropdown = false
      if (newMode === 'performance' && !this.perfData.length) this.loadData('performance')
      if (newMode === 'rayg' && !this.raygData.length) this.loadData('rayg')
      if (newMode === 'hubAnalytics') this.loadData('hubAnalytics')
    }
  },

  mounted() {
    try {
      const enc = window.localStorage.getItem('userdetail')
      if (enc) {
        const bytes = CryptoJS.AES.decrypt(enc.toString(), 'Key')
        const user = JSON.parse(bytes.toString(CryptoJS.enc.Utf8))
        this.hubId = user[0].HubId[0]
      } else {
        this.hubId = 1001
      }
    } catch (_) {
      this.hubId = 1001
    }
    this.loadData()
    document.addEventListener('click', this.handleOutsideClick)
  },

  destroyed() {
    document.removeEventListener('click', this.handleOutsideClick)
  },

  methods: {
    isToday: function () {
      return moment(this.selectedDate).isSame(moment(), 'day')
    },
    aggregateHubHourlyTotals(employees) {
      const hourly = Array(24).fill(0)
      if (!employees || !employees.length) return hourly

      const types = this.hubAnalyticsFilters.selectedTypes || []
      const selectedCats = this.hubAnalyticsFilters.selectedCategories || []
      const selectedBuckets = this.hubAnalyticsFilters.selectedBuckets || []
      const hasBucketFilter = selectedCats.length > 0 || selectedBuckets.length > 0

      const bucketMatches = (b) => {
        if (!b) return false
        if (!hasBucketFilter) return true
        const cat = b.mainBucket || 'Other'
        const name = b.bucketName
        if (selectedBuckets.length && selectedBuckets.indexOf(name) !== -1) return true
        if (selectedBuckets.length && selectedCats.length === 0) return false
        if (selectedCats.length && selectedCats.indexOf(cat) !== -1) {
          if (!selectedBuckets.length) return true
          return selectedBuckets.indexOf(name) !== -1
        }
        return false
      }

      employees.forEach(emp => {
        if (!emp) return
        if (types.length && types.indexOf(emp.employeeType || 'Other') === -1) return

        if (hasBucketFilter) {
          ;(emp.buckets || []).filter(bucketMatches).forEach(b => {
            ;(b.hourlyScans || []).forEach((val, h) => {
              if (h >= 0 && h < 24) hourly[h] += val || 0
            })
          })
        } else {
          const src = (emp.aggregate && emp.aggregate.hourlyScans) || []
          src.forEach((val, h) => {
            if (h >= 0 && h < 24) hourly[h] += val || 0
          })
        }
      })
      return hourly
    },

    async loadData(mode) {
      if (!mode) mode = this.globalViewMode
      this.isLoading = true

      const filters = mode === 'performance'
        ? this.perfFilters
        : (mode === 'hubAnalytics' ? this.hubAnalyticsFilters : this.raygFilters)

      // Keep selectedDays in sync with the active filter's days to avoid slicing/array size bugs
      this.selectedDays = filters.days

      // Default view mode based on selection
      this.viewMode = filters.days === 1 ? 'matrix' : 'summary'

      const payload = {
        huid: this.hubId,
        date: moment(filters.date).format('YYYY-MM-DD'),
        days: filters.days
      }

      try {
        if (mode === 'hubAnalytics') {
          const analysisDate = moment(filters.date)
          const weeksAgo = Math.min(7, Math.max(1, parseInt(filters.compareWeeksAgo, 10) || 1))
          const lastWeekDate = analysisDate.clone().subtract(weeksAgo * 7, 'days')
          const comparePayload7 = {
            huid: this.hubId,
            date: analysisDate.format('YYYY-MM-DD'),
            days: 7
          }
          const comparePayloadWeek = {
            huid: this.hubId,
            date: lastWeekDate.format('YYYY-MM-DD'),
            days: 1
          }

          const dayPayload = {
            huid: this.hubId,
            date: analysisDate.format('YYYY-MM-DD'),
            days: 1
          }

          const [mainRes, last7Res, lastWeekRes, dayRes] = await Promise.all([
            axios.post(`${apiUrl.api_url_hubops}productivity/getTrackingData`, payload),
            axios.post(`${apiUrl.api_url_hubops}productivity/getTrackingData`, comparePayload7),
            axios.post(`${apiUrl.api_url_hubops}productivity/getTrackingData`, comparePayloadWeek),
            filters.days === 1
              ? Promise.resolve(null)
              : axios.post(`${apiUrl.api_url_hubops}productivity/getTrackingData`, dayPayload)
          ])

          const res = mainRes.data
          if (res.ResultCode === 100) {
            this.hubName = res.ResponseData.hubName
            this.hubAnalyticsData = res.ResponseData.employees || []
            this.summary = Object.assign({}, this.summary, res.ResponseData.summary)
          } else {
            this.$alertify.error(res.ReturnMessage || 'Failed to load productivity data')
          }

          this.hubHourlyDayData = filters.days === 1
            ? (this.hubAnalyticsData || [])
            : ((dayRes && dayRes.data && dayRes.data.ResultCode === 100)
              ? (dayRes.data.ResponseData.employees || [])
              : [])

          this.hubCompareLast7Data = (last7Res.data && last7Res.data.ResultCode === 100)
            ? (last7Res.data.ResponseData.employees || [])
            : []
          this.hubCompareLastWeekData = (lastWeekRes.data && lastWeekRes.data.ResultCode === 100)
            ? (lastWeekRes.data.ResponseData.employees || [])
            : []
          this.hubCompareMeta = {
            lastWeekDate: lastWeekDate.format('YYYY-MM-DD'),
            weeksAgo,
            lastWeekLabel: weeksAgo === 1
              ? `Same ${lastWeekDate.format('ddd')} · 1 week ago (${lastWeekDate.format('DD MMM')})`
              : `Same ${lastWeekDate.format('ddd')} · ${weeksAgo} weeks ago (${lastWeekDate.format('DD MMM')})`
          }
        } else {
          const response = await axios.post(`${apiUrl.api_url_hubops}productivity/getTrackingData`, payload)
          const res = response.data

          if (res.ResultCode === 100) {
            this.hubName = res.ResponseData.hubName
            if (mode === 'performance') {
              this.perfData = res.ResponseData.employees
              this.summary = Object.assign({}, this.summary, res.ResponseData.summary)
            } else {
              this.raygData = res.ResponseData.employees
            }
          } else {
            this.$alertify.error(res.ReturnMessage || 'Failed to load productivity data')
          }
        }
      } catch (err) {
        console.error('API Error:', err)
        this.$alertify.error('Unable to connect to HubOps API. Please check backend status.')
      } finally {
        this.isLoading = false
      }
    },

    toggleExpand(userId) {
      if (this.expandedEmp === userId) this.expandedEmp = null
      else this.expandedEmp = userId
    },

    isExpanded(userId) {
      return this.expandedEmp === userId
    },

    prevPage() {
      if (this.currentPage > 1) this.currentPage--
    },

    toggleSort(type) {
      if (this.sortBy === type) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = type
        this.sortOrder = type === 'performance' ? 'desc' : 'asc'
      }
      this.currentPage = 1
    },

    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++
    },

    getBucketHourly(emp, bName) {
      const bucket = emp.buckets.find(b => b.bucketName === bName)
      return bucket ? bucket.hourlyScans : Array(24).fill(0)
    },

    getBucketDaily(emp, bName) {
      const bucket = emp.buckets.find(b => b.bucketName === bName)
      const daily = bucket ? (bucket.dailyBreakdown || []).slice(0, this.selectedDays) : Array(this.selectedDays).fill(0)
      return daily.map(v => parseFloat(v || 0).toFixed(2))
    },

    getBucketTotal(emp, bName) {
      const bucket = emp.buckets.find(b => b.bucketName === bName)
      return bucket ? bucket.totalScans : 0
    },

    getBucketMHR(emp, bName) {
      const bucket = emp.buckets.find(b => b.bucketName === bName)
      return bucket ? parseFloat(bucket.totalMHR || 0).toFixed(2) : '0.00'
    },

    getGroupDailyTotal(emp, groupName) {
      const group = emp.categoryGroups.find(g => g.name === groupName)
      if (!group) return Array(this.selectedDays).fill('0.00')

      const dailyTotals = Array(this.selectedDays).fill(0)
      group.buckets.forEach(bName => {
        const daily = this.getBucketDaily(emp, bName)
        daily.forEach((val, i) => dailyTotals[i] += parseFloat(val || 0))
      })
      return dailyTotals.map(v => v.toFixed(2))
    },

    getGroupMHR(emp, groupName) {
      const group = emp.categoryGroups.find(g => g.name === groupName)
      if (!group) return '0.00'
      let totalMHR = 0
      group.buckets.forEach(bName => {
        totalMHR += parseFloat(this.getBucketMHR(emp, bName) || 0)
      })
      return totalMHR.toFixed(2)
    },

    getRaygClass(emp, index) {
      return this.getAggregateStatus(emp, index)
    },

    getAggregateScans(emp, index) {
      let total = 0
      emp.buckets.forEach(b => {
        total += this.viewMode === 'matrix' ? (b.hourlyScans[index] || 0) : (b.dailyBreakdown[index] || 0)
      })
      return total || ''
    },

    getAggregateMHR(emp, index) {
      let totalMHR = 0
      emp.buckets.forEach(b => {
        const val = this.viewMode === 'matrix' ? (b.hourlyScans[index] || 0) : (b.dailyBreakdown[index] || 0)
        if (this.viewMode === 'summary') {
          totalMHR += parseFloat(val || 0)
        } else {
          const benchmark = b.benchmark || 250
          totalMHR += val / benchmark
        }
      })
      return totalMHR > 0 ? totalMHR.toFixed(2) : ''
    },

    getAggregateStatus(emp, index) {
      const mhrStr = this.getAggregateMHR(emp, index)
      if (!mhrStr) return ''

      const totalMHR = parseFloat(mhrStr)

      // Differentiate hourly cells (matrix) vs daily cells (summary)
      if (this.viewMode === 'matrix') {
        // Hourly efficiency thresholds
        if (totalMHR < 0.4) return 'critical'        // <40% efficiency
        if (totalMHR < 0.7) return 'warning'         // <70% efficiency
        if (totalMHR < 0.9) return 'good'            // <90% efficiency
        if (totalMHR < 1.1) return 'excellent'       // <110% efficiency
        return 'excellent-plus'                      // >=110% efficiency
      } else {
        // Daily standard MHR thresholds
        if (totalMHR < 3) return 'critical'          // <3.0 Hrs
        if (totalMHR < 5) return 'warning'           // <5.0 Hrs
        if (totalMHR < 7) return 'good'              // <7.0 Hrs
        if (totalMHR < 8) return 'excellent'         // <8.0 Hrs
        return 'excellent-plus'                      // >=8.0 Hrs
      }
    },

    getColumnStatusCount(index, statusType) {
      const typeMap = { 'critical': 'critical', 'warning': 'warning', 'good': 'good', 'excellent': 'excellent', 'excellent-plus': 'excellent-plus' }
      const targetType = typeMap[statusType]

      return this.filteredData.filter(emp => {
        return this.getAggregateStatus(emp, index) === targetType
      }).length
    },

    getColumnTotalMHR(index) {
      let total = 0
      this.filteredData.forEach(emp => {
        const mhr = parseFloat(this.getAggregateMHR(emp, index))
        if (!isNaN(mhr)) total += mhr
      })
      return total.toFixed(2)
    },

    getStatusClass(rayg) {
      const map = { 'R': 'critical', 'A': 'warning', 'Y': 'good', 'G': 'excellent', 'B': 'excellent-plus', 'N': 'no-scan' }
      return map[rayg] || ''
    },

    getStatusLabel(rayg) {
      const map = { 'R': '0-3 Hrs', 'A': '3-5 Hrs', 'Y': '5-7 Hrs', 'G': '7-8 Hrs', 'B': '8+ Hrs' }
      return map[rayg] || rayg
    },

    getStatusRows() {
      return [
        { id: 'critical', label: 'Critical Count' },
        { id: 'warning', label: 'Warning Count' },
        { id: 'good', label: 'Good Count' },
        { id: 'excellent', label: 'Excellent Count' },
        { id: 'excellent-plus', label: 'Excellent Plus Count' }
      ]
    },

    getLegendItems() {
      return [
        { id: 'critical', label: 'Critical' },
        { id: 'warning', label: 'Warning' },
        { id: 'good', label: 'Good' },
        { id: 'excellent', label: 'Excellent' },
        { id: 'excellent-plus', label: 'Excellent Plus' }
      ]
    },

    exportPerformanceCSV() {
      this.$alertify.success("Exporting Performance Report...")
      
      const data = this.filteredData
      if (!data || !data.length) return

      const filters = this.activeFilters
      const days = filters.days

      if (days === 1) {
        // ── SINGLE-DAY PERFORMANCE: Exact 24h Hourly Audit Log ──
        const headers = [
          "Date", "Employee Name", "Employee ID", "Employee Type", "Email Address",
          "00:00", "01:00", "02:00", "03:00", "04:00", "05:00",
          "06:00", "07:00", "08:00", "09:00", "10:00", "11:00",
          "12:00", "13:00", "14:00", "15:00", "16:00", "17:00",
          "18:00", "19:00", "20:00", "21:00", "22:00", "23:00",
          "Total Scans (Day)", "Total MHR (Day)", "RAYG Status"
        ]

        const rows = data.map(e => {
          const hourly = e.aggregate.hourlyScans || Array(24).fill(0)
          return [
            moment(filters.date).format('YYYY-MM-DD'),
            e.employeeName,
            e.employeeId,
            e.employeeType,
            e.empCode || e.email || '',
            ...hourly,
            e.aggregate.totalScans,
            e.aggregate.totalMHR,
            this.getStatusLabel(e.aggregate.rayg)
          ]
        })

        let csvContent = "data:text/csv;charset=utf-8," 
          + headers.join(",") + "\n"
          + rows.map(r => r.join(",")).join("\n")

        const encodedUri = encodeURI(csvContent)
        const link = document.createElement("a")
        link.setAttribute("href", encodedUri)
        link.setAttribute("download", `Performance_Hourly_Audit_${moment(filters.date).format('YYYYMMDD')}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        // ── MULTI-DAY PERFORMANCE: 360-degree Roster Diagnostics Report ──
        const datesList = []
        for (let i = 0; i < days; i++) {
          datesList.push(moment(filters.date).subtract(i, 'days').format('DD/MM'))
        }
        datesList.reverse()

        const headers = [
          "Employee Name", "Employee ID", "Employee Type", "Email Address",
          "Metric Profile",
          "00:00 / Day 1", "01:00 / Day 2", "02:00 / Day 3", "03:00 / Day 4", "04:00 / Day 5",
          "05:00 / Day 6", "06:00 / Day 7", "07:00 / Day 8", "08:00 / Day 9", "09:00 / Day 10",
          "10:00 / Day 11", "11:00 / Day 12", "12:00 / Day 13", "13:00 / Day 14", "14:00 / Day 15",
          "15:00 / --", "16:00 / --", "17:00 / --", "18:00 / --", "19:00 / --", "20:00 / --",
          "21:00 / --", "22:00 / --", "23:00 / --",
          "Total Scans (Period)", "Total MHR (Period)", "RAYG Status (Period)"
        ]

        const rows = []

        data.forEach(e => {
          // Row 1: Hourly Operational Persona Profile
          const hourly = e.aggregate.hourlyScans || Array(24).fill(0)
          rows.push([
            e.employeeName,
            e.employeeId,
            e.employeeType,
            e.empCode || e.email || '',
            "Hourly Persona (Period Aggregate Scans)",
            ...hourly,
            e.aggregate.totalScans,
            e.aggregate.totalMHR,
            this.getStatusLabel(e.aggregate.rayg)
          ])

          // Row 2: Daily Man-Hour Consistency Trend
          const dailyTrendCells = Array(24).fill("")

          datesList.forEach((dateLabel, idx) => {
            let dayMHR = 0
            if (e.buckets) {
              e.buckets.forEach(b => {
                if (b && b.dailyBreakdown && b.dailyBreakdown[idx] !== undefined) {
                  dayMHR += parseFloat(b.dailyBreakdown[idx] || 0)
                }
              })
            }
            if (idx < 24) {
              dailyTrendCells[idx] = `${dateLabel}: ${dayMHR.toFixed(2)} Hrs`
            }
          })

          rows.push([
            e.employeeName,
            e.employeeId,
            e.employeeType,
            e.empCode || e.email || '',
            "Daily Man-Hour Trend Breakdown",
            ...dailyTrendCells,
            e.aggregate.totalScans,
            e.aggregate.totalMHR,
            this.getStatusLabel(e.aggregate.rayg)
          ])
        })

        let csvContent = "data:text/csv;charset=utf-8," 
          + headers.join(",") + "\n"
          + rows.map(r => r.join(",")).join("\n")

        const encodedUri = encodeURI(csvContent)
        const link = document.createElement("a")
        link.setAttribute("href", encodedUri)
        link.setAttribute("download", `Performance_Roster_Diagnostics_${moment(filters.date).format('YYYYMMDD')}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },

    exportHubAnalyticsCSV() {
      const s = this.hubAnalyticsSummary
      if (!s.rosterCount) {
        this.$alertify.error('No hub analytics data to export.')
        return
      }
      const headers = [
        'Hub', 'Period', 'Roster', 'Active scanners', 'Utilization %', 'Hub performance score',
        'Total scans', 'Total MHR', 'Avg MHR / active', 'Avg productivity index', 'Peak hour'
      ]
      const summaryRow = [
        this.hubName,
        s.periodLabel,
        s.rosterCount,
        s.activeCount,
        s.utilizationPct,
        s.hubPerformanceScore,
        s.totalScans,
        s.totalMHR,
        s.avgMhrPerActive,
        s.avgProdIndex,
        s.peakHour !== null ? `${s.peakHour}:00` : ''
      ]
      const wsHeaders = ['Workstream', 'Employees', 'Active', 'Scans', 'MHR']
      const wsRows = s.workstreams.map(w => [w.workstream, w.employees, w.active, w.scans, w.mhr.toFixed(2)])
      const catHeaders = ['Work category', 'Scans', 'MHR']
      const catRows = s.categories.map(c => [c.category, c.scans, c.mhr.toFixed(2)])

      const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`
      const lines = [
        headers.map(esc).join(','),
        summaryRow.map(esc).join(','),
        '',
        wsHeaders.map(esc).join(','),
        ...wsRows.map(r => r.map(esc).join(',')),
        '',
        catHeaders.map(esc).join(','),
        ...catRows.map(r => r.map(esc).join(','))
      ]
      const csvContent = 'data:text/csv;charset=utf-8,' + lines.join('\n')
      const link = document.createElement('a')
      link.setAttribute('href', encodeURI(csvContent))
      link.setAttribute('download', `Hub_Analytics_${this.hubId}_${moment(this.hubAnalyticsFilters.date).format('YYYYMMDD')}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.$alertify.success('Hub analytics report exported.')
    },

    exportRaygCSV() {
      this.$alertify.success("Exporting RAYG Grid...")
      const headers = ["Employee Name", "Employee ID", "Employee Type", "Email", "Total Scans", "Total MHR", "RAYG Status"]
      const rows = this.filteredData.map(e => [
        e.employeeName,
        e.employeeId,
        e.employeeType,
        e.empCode || e.email || '',
        e.aggregate.totalScans,
        e.aggregate.totalMHR,
        this.getStatusLabel(e.aggregate.rayg)
      ])
      this.downloadCSV(headers, rows, "RAYG_Grid_Report")
    },

    async exportGeographicCSV() {
      this.isLoading = true;
      this.$alertify.success("Fetching Geographic Report Data...");
      try {
        const fromDate = this.geoFilters.fromDate;
        const toDate = this.geoFilters.toDate;

        if (!fromDate || !toDate) {
          this.$alertify.error("Please select both From Date and To Date.");
          this.isLoading = false;
          return;
        }

        if (moment(fromDate).isAfter(moment(toDate))) {
          this.$alertify.error("From Date cannot be after To Date.");
          this.isLoading = false;
          return;
        }

        const days = moment(toDate).diff(moment(fromDate), 'days') + 1;
        const targetDate = toDate;

        const payload = {
          date: moment(targetDate).format('YYYY-MM-DD'),
          days: days,
          zone_name: this.geoFilters.zone_name || '',
          state_name: this.geoFilters.state_name || '',
          city_name: this.geoFilters.city_name || '',
          facility_id: this.geoFilters.facility_id ? parseInt(this.geoFilters.facility_id) : 0
        };

        const response = await axios.post(`${apiUrl.api_url_hubops}productivity/getGeographicTrackingData`, payload);
        const res = response.data;
        if (res.ResultCode === 100) {
          const rawData = res.ResponseData.employees || [];
          if (!rawData.length) {
            this.$alertify.error("No data found for the selected geographic filters.");
            return;
          }

          // Apply client-side filters (Search, Status, Employee Types)
          const searchId = this.geoFilters.searchId.trim().toLowerCase();
          let processed = rawData;
          if (searchId) {
            processed = processed.filter(e => 
              (e.employeeId || '').toString().toLowerCase().includes(searchId) || 
              (e.empCode || '').toString().toLowerCase().includes(searchId) || 
              (e.employeeName || '').toString().toLowerCase().includes(searchId) || 
              (e.email || '').toString().toLowerCase().includes(searchId)
            );
          }
          if (this.geoFilters.selectedTypes.length > 0) {
            processed = processed.filter(e => this.geoFilters.selectedTypes.includes(e.employeeType || 'Other'));
          }

          if (!processed.length) {
            this.$alertify.error("No data available after applying client filters.");
            return;
          }

          // Generate CSV
          if (days === 1) {
            const headers = [
              "Date", "Employee Name", "Employee ID", "Employee Type", "Business Vertical", "Sub Vertical", "Function", "Pay Type", "Designation", "Employee Category", "Emp Type", "Work Stream", "Email Address",
              "Date Of Joining", "Date Of Relieving",
              "Zone", "State", "City", "Hub",
              "00:00", "01:00", "02:00", "03:00", "04:00", "05:00",
              "06:00", "07:00", "08:00", "09:00", "10:00", "11:00",
              "12:00", "13:00", "14:00", "15:00", "16:00", "17:00",
              "18:00", "19:00", "20:00", "21:00", "22:00", "23:00",
              "Total Scans (Day)", "Total MHR (Day)", "Active Hours"
            ];
            const rows = processed.map(e => {
              const hourly = e.aggregate.hourlyScans || Array(24).fill(0);
              const activeHours = hourly.filter(s => s > 0).length;
              return [
                moment(targetDate).format('YYYY-MM-DD'),
                e.employeeName, e.employeeId, e.emptypename || e.employeeType || '',
                e.bussinessverticalname || 'N/A', e.subverticalname || 'N/A', e.functionname || 'N/A', e.paytypename || 'N/A', e.designationname || 'N/A',
                e.empcategoryname || 'N/A', e.emptypename || 'N/A', e.workstream || 'Unclassified',
                e.empCode || e.email || '',
                e.dateofjoining || 'N/A', e.dateofrelieving || 'N/A',
                e.zoneName, e.stateName, e.cityName, e.facilityName,
                ...hourly, e.aggregate.totalScans, e.aggregate.totalMHR, activeHours
              ];
            });
            this.downloadCSV(headers, rows, `Geographic_Hourly_Audit_${moment(targetDate).format('YYYYMMDD')}`);
          } else {
            const datesList = [];
            for (let i = 0; i < days; i++) {
              datesList.push(moment(targetDate).subtract(i, 'days').format('DD-MM-YYYY'));
            }
            datesList.reverse();

            const activeHoursHeaders = datesList.map(dateStr => `${dateStr} (Active Hours)`);
            const headers = [
              "Employee Name", "Employee ID", "Employee Type", "Business Vertical", "Sub Vertical", "Function", "Pay Type", "Designation", "Employee Category", "Emp Type", "Work Stream", "Email Address",
              "Date Of Joining", "Date Of Relieving",
              "Zone", "State", "City", "Hub", 
              ...datesList,
              "Total MHR (Period)", "Active Hours",
              ...activeHoursHeaders
            ];

            const rows = [];
            processed.forEach(e => {
              const dailyMHRs = [];
              const dailyActiveHours = [];
              let activeDaysCount = 0;
              
              datesList.forEach(dateStr => {
                // The backend array dailyBreakdown is now a precise YYYY-MM-DD object map!
                // We just convert the header 'DD-MM-YYYY' to the backend key 'YYYY-MM-DD' and pull it safely!
                const backendKey = moment(dateStr, 'DD-MM-YYYY').format('YYYY-MM-DD');
                
                let dayMHR = 0;
                if (e.buckets) {
                  e.buckets.forEach(b => {
                    if (b && b.dailyBreakdown && b.dailyBreakdown[backendKey] !== undefined) {
                      dayMHR += parseFloat(b.dailyBreakdown[backendKey] || 0);
                    }
                  });
                }
                if (dayMHR > 0) {
                  activeDaysCount++;
                }
                dailyMHRs.push(dayMHR.toFixed(2));

                // Pull daily active hours from aggregate response object
                const dayActiveCount = e.aggregate.dailyActiveHours ? (e.aggregate.dailyActiveHours[backendKey] || 0) : 0;
                dailyActiveHours.push(dayActiveCount);
              });

              const totalMhrVal = parseFloat(e.aggregate.totalMHR || 0);
              const activeHours = e.aggregate.activeHours || 0;

              rows.push([
                e.employeeName, e.employeeId, e.emptypename || e.employeeType || '',
                e.bussinessverticalname || 'N/A', e.subverticalname || 'N/A', e.functionname || 'N/A', e.paytypename || 'N/A', e.designationname || 'N/A',
                e.empcategoryname || 'N/A', e.emptypename || 'N/A', e.workstream || 'Unclassified',
                e.empCode || e.email || '',
                e.dateofjoining || 'N/A', e.dateofrelieving || 'N/A',
                e.zoneName, e.stateName, e.cityName, e.facilityName,
                ...dailyMHRs,
                totalMhrVal.toFixed(2), 
                activeHours,
                ...dailyActiveHours
              ]);
            });
            this.downloadCSV(headers, rows, `Geographic_Daily_MHR_Audit_${moment(targetDate).format('YYYYMMDD')}`);
          }
          this.$alertify.success(`Successfully exported geographic report across ${res.ResponseData.hubCount} hubs.`);
        } else {
          this.$alertify.error(res.ReturnMessage || "Failed to fetch geographic data.");
        }
      } catch (err) {
        console.error('Geographic Export Error:', err);
        this.$alertify.error("Unable to connect to backend for geographic export.");
      } finally {
        this.isLoading = false;
      }
    },

    downloadCSV(headers, rows, fileName) {
      if (!rows || !rows.length) return;
      
      const esc = (v) => {
        if (v === null || v === undefined) return '""';
        const str = String(v);
        return `"${str.replace(/"/g, '""')}"`;
      };
      
      const csvContent = "data:text/csv;charset=utf-8," 
        + headers.map(esc).join(",") + "\n"
        + rows.map(r => r.map(esc).join(",")).join("\n");

      const encodedUri = encodeURI(csvContent)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("download", `${fileName}_${moment().format('YYYYMMDD')}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    logout() {
      localStorage.clear()
      this.$router.push('/login')
    },

    toggleStatusFilter(status) {
      const filters = this.globalViewMode === 'performance' ? this.perfFilters : this.raygFilters
      if (filters.status === status) {
        filters.status = null
      } else {
        filters.status = status
      }
      if (this.globalViewMode === 'performance') this.currentPage = 1
    },

    getChartOptions(emp) {
      const categories = this.summaryDates

      return {
        chart: {
          type: 'bar',
          height: 200,
          stacked: true, // Enable stacking for bucket bifurcation
          toolbar: { show: false },
          animations: { enabled: true }
        },
        plotOptions: {
          bar: {
            borderRadius: 0,
            columnWidth: '50%',
          }
        },
        dataLabels: { enabled: false },
        // Use a diverse color palette for different buckets
        colors: ['#FF6700', '#3b82f6', '#10b981', '#f59e0b', '#6366f1'],
        xaxis: {
          categories: categories,
          labels: { style: { fontSize: '10px', fontWeight: 600 } }
        },
        yaxis: {
          title: { text: 'Scans', style: { fontSize: '10px', fontWeight: 600 } },
          labels: { style: { fontSize: '10px' } }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'right',
          fontSize: '11px',
          fontWeight: 500,
          markers: { radius: 12 }
        },
        grid: {
          borderColor: '#f1f1f1',
          strokeDashArray: 4
        },
        tooltip: {
          theme: 'dark',
          shared: true,
          intersect: false
        }
      }
    },

    getChartSeries(emp) {
      const days = this.selectedDays
      // Identify all unique main buckets for this employee
      const mainBuckets = emp.buckets.map(function (b) { return b.mainBucket }).filter(function (item, pos, self) { return self.indexOf(item) === pos })

      const series = mainBuckets.map(mb => {
        const dailyData = Array(days).fill(0)

        // Find all sub-buckets belonging to this main bucket
        const subBuckets = emp.buckets.filter(b => b.mainBucket === mb)

        subBuckets.forEach(sb => {
          (sb.dailyBreakdown || []).slice(0, days).forEach((val, i) => {
            dailyData[i] += val
          })
        })

        return {
          name: mb,
          data: dailyData
        }
      })

      return series
    },

    toggleTypeDropdown(e) {
      if (e) e.stopPropagation()
      this.showTypeDropdown = !this.showTypeDropdown
      this.showHubCategoryDropdown = false
      this.showHubBucketDropdown = false
    },

    closeTypeDropdown() {
      this.showTypeDropdown = false
    },

    toggleTypeSelection(type) {
      const filters = this.activeFilters
      const idx = filters.selectedTypes.indexOf(type)
      if (idx > -1) {
        filters.selectedTypes.splice(idx, 1)
      } else {
        filters.selectedTypes.push(type)
      }
      if (this.globalViewMode === 'performance') this.currentPage = 1
    },

    toggleHubCategoryDropdown(e) {
      if (e) e.stopPropagation()
      this.showHubCategoryDropdown = !this.showHubCategoryDropdown
      this.showHubBucketDropdown = false
      this.showTypeDropdown = false
    },

    toggleHubBucketDropdown(e) {
      if (e) e.stopPropagation()
      this.showHubBucketDropdown = !this.showHubBucketDropdown
      this.showHubCategoryDropdown = false
      this.showTypeDropdown = false
    },

    toggleHubCategorySelection(category) {
      const list = this.hubAnalyticsFilters.selectedCategories
      const idx = list.indexOf(category)
      if (idx > -1) list.splice(idx, 1)
      else list.push(category)
      // Drop bucket selections that no longer belong to selected categories
      if (list.length) {
        const allowed = this.availableHubBuckets
        this.hubAnalyticsFilters.selectedBuckets = this.hubAnalyticsFilters.selectedBuckets.filter(b => allowed.indexOf(b) !== -1)
      }
    },

    toggleHubBucketSelection(bucket) {
      const list = this.hubAnalyticsFilters.selectedBuckets
      const idx = list.indexOf(bucket)
      if (idx > -1) list.splice(idx, 1)
      else list.push(bucket)
    },

    setHubCategoryFilter(category) {
      const list = this.hubAnalyticsFilters.selectedCategories
      if (list.length === 1 && list[0] === category) {
        this.hubAnalyticsFilters.selectedCategories = []
      } else {
        this.hubAnalyticsFilters.selectedCategories = [category]
        this.hubAnalyticsFilters.selectedBuckets = []
      }
    },

    setHubBucketFilter(bucket) {
      const list = this.hubAnalyticsFilters.selectedBuckets
      if (list.length === 1 && list[0] === bucket) {
        this.hubAnalyticsFilters.selectedBuckets = []
      } else {
        this.hubAnalyticsFilters.selectedBuckets = [bucket]
      }
    },

    clearHubBucketFilters() {
      this.hubAnalyticsFilters.selectedCategories = []
      this.hubAnalyticsFilters.selectedBuckets = []
    },

    handleOutsideClick(e) {
      const selects = this.$el.querySelectorAll('.custom-multiselect')
      let inside = false
      for (let i = 0; i < selects.length; i++) {
        if (selects[i].contains(e.target)) {
          inside = true
          break
        }
      }
      if (!inside) {
        this.showTypeDropdown = false
        this.showHubCategoryDropdown = false
        this.showHubBucketDropdown = false
      }
    }
  },
}



// WEBPACK FOOTER //
// ./src/views/components/ProductivityTracking/ProductivityTracking.js